import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import styles from './admin.module.css';

const BlogManagement = () => {
  const { theme, toggleTheme } = useTheme();
  const { token } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  // Updated: Added description and image to formData
  const [formData, setFormData] = useState({ title: '', description: '', content: '', imageUrl: '', isPublished: false });
  const [showConfirmDialog, setShowConfirmDialog] = useState({ show: false, blogId: '', title: '' });
  const API_URL = import.meta.env.VITE_API_URL;

  // Fetch blogs from API
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/blogs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch blogs: ${response.status}`);
      }
      const data = await response.json();
      setBlogs(data.blogs || data);
    } catch (err) {
      setError(err.message || 'Failed to fetch blogs');
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Handle form submit (create or update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // Updated: Check for imageUrl instead of image
    if (!formData.title.trim() || !formData.description.trim() || !formData.content.trim()) {
      showNotification('Title, description, and content are required', 'error');
      return;
    }
    try {
      const url = editingBlog ? `${API_URL}/blogs/${editingBlog._id}` : `${API_URL}/blogs`;
      const method = editingBlog ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),  // Now includes imageUrl
      });
      if (!response.ok) {
        throw new Error(`Failed to ${editingBlog ? 'update' : 'create'} blog: ${response.status}`);
      }
      const data = await response.json();
      if (editingBlog) {
        setBlogs(blogs.map(blog => blog._id === editingBlog._id ? data.blog : blog));
        showNotification('Blog updated successfully');
      } else {
        setBlogs([...blogs, data.blog]);
        showNotification('Blog created successfully');
      }
      resetForm();
    } catch (err) {
      showNotification(err.message || `Failed to ${editingBlog ? 'update' : 'create'} blog`, 'error');
      console.error('Error submitting blog:', err);
    }
  };

  // Delete blog
  const deleteBlog = async (blogId) => {
    try {
      const response = await fetch(`${API_URL}/blogs/${blogId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to delete blog: ${response.status}`);
      }
      setBlogs(blogs.filter(blog => blog._id !== blogId));
      showNotification('Blog deleted successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to delete blog', 'error');
      console.error('Error deleting blog:', err);
    } finally {
      setShowConfirmDialog({ show: false, blogId: '', title: '' });
    }
  };

  // Handle edit
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    // Updated: Use imageUrl
    setFormData({ title: blog.title, description: blog.description || '', content: blog.content || '', imageUrl: blog.imageUrl || '', isPublished: blog.isPublished });
    setShowForm(true);
  };

  // Reset form
  const resetForm = () => {
    // Updated: Reset imageUrl
    setFormData({ title: '', description: '', content: '', imageUrl: '', isPublished: false });
    setEditingBlog(null);
    setShowForm(false);
  };

  // Handle confirm delete
  const handleConfirmDelete = () => {
    deleteBlog(showConfirmDialog.blogId);
  };

  // Initial fetch
  useEffect(() => {
    fetchBlogs();
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className={styles.blogManagement}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Blog Management</h1>
        <button
          onClick={toggleTheme}
          className={styles.themeToggle}
        >
          {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
        </button>
      </div>

      {/* Notification */}
      {notification.show && (
        <div className={`${styles.alert} ${notification.type === 'success' ? styles.alertSuccess : styles.alertDanger}`}>
          <div className="d-flex align-items-center">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 me-2" />
            ) : (
              <AlertCircle className="w-5 h-5 me-2" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div className={`${styles.alert} ${styles.alertDanger}`}>{error}</div>}

      {/* Create/Edit Form */}
      {showForm && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h5 className={styles.cardTitle}>{editingBlog ? 'Edit Blog' : 'Create New Blog'}</h5>
          </div>
          <div className={styles.cardBody}>
            <form onSubmit={handleFormSubmit}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              {/* New: Description field */}
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Content</label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                />
              </div>
              {/* New: Image URL field */}
              <div className="mb-3">
                <label className="form-label">Image URL (Optional)</label>
                <input
                  type="url"
                  className="form-control"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                />
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                />
                <label className="form-check-label">Published</label>
              </div>
              <div className="d-flex gap-2">
                <button type="submit" className={styles.buttonPrimary} style={{ borderRadius: '8px', padding: '0.5rem 1rem' }}>
                  {editingBlog ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={resetForm} className={styles.buttonSecondary} style={{ borderRadius: '8px', padding: '0.5rem 1rem' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog.show && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h5 className={styles.modalTitle}>Confirm Delete</h5>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setShowConfirmDialog({ show: false, blogId: '', title: '' })}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className="text-muted mb-4">
                Are you sure you want to delete the blog "{showConfirmDialog.title}"? This action cannot be undone.
              </p>
              <div className="d-flex gap-3">
                <button
                  onClick={handleConfirmDelete}
                  className={styles.buttonDanger}
                  style={{ borderRadius: '8px', padding: '0.5rem 1rem' }}
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => setShowConfirmDialog({ show: false, blogId: '', title: '' })}
                  className={styles.buttonSecondary}
                  style={{ borderRadius: '8px', padding: '0.5rem 1rem' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blogs List */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className={styles.cardTitle}>Blogs Overview</h5>
            <button
              onClick={() => setShowForm(true)}
              className={styles.buttonPrimary}
              style={{ borderRadius: '8px', padding: '0.5rem 1rem' }}
            >
              <Plus className="w-4 h-4 me-1" />
              New Blog
            </button>
          </div>
        </div>
        <div className={styles.cardBody}>
          {blogs.length === 0 ? (
            <div className={`text-center py-5 ${styles.emptyState}`}>
              <div className={styles.emptyStateIcon}>📝</div>
              <p>No blogs found. Create your first blog post!</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className={`table ${styles.table}`}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((blog) => (
                    <tr key={blog._id}>
                      <td>
                        <div className="fw-medium">{blog.title}</div>
                      </td>
                      <td>
                        <span className={`badge ${blog.isPublished ? 'bg-success' : 'bg-secondary'}`}>
                          {blog.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            onClick={() => handleEdit(blog)}
                            className={styles.buttonPrimary}
                            style={{ borderRadius: '8px', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                          >
                            <Edit className="w-3 h-3 me-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => setShowConfirmDialog({ show: true, blogId: blog._id, title: blog.title })}
                            className={styles.buttonDanger}
                            style={{ borderRadius: '8px', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                          >
                            <Trash2 className="w-3 h-3 me-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManagement;